import {
  Children,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface RouterLocation {
  pathname: string;
}

interface RouterContextValue {
  location: RouterLocation;
  navigate: (to: string) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

interface MemoryRouterProps {
  initialEntries?: string[];
  children: ReactNode;
}

export function MemoryRouter({ initialEntries = ['/'], children }: MemoryRouterProps) {
  const normalized = initialEntries.length > 0 ? initialEntries : ['/'];
  const [entries, setEntries] = useState<string[]>(normalized);
  const [index, setIndex] = useState<number>(normalized.length - 1);

  const pathname = entries[index] ?? '/';

  const navigate = useCallback((to: string) => {
    const destination = to.startsWith('/') ? to : `/${to}`;
    setEntries((current) => [...current.slice(0, index + 1), destination]);
    setIndex((current) => current + 1);
  }, [index]);

  const value = useMemo<RouterContextValue>(() => ({ location: { pathname }, navigate }), [pathname, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

interface RouteProps {
  path: string;
  element: ReactNode;
}

export function Route(_: RouteProps): null {
  return null;
}

Route.displayName = 'SimpleRoute';

interface RoutesProps {
  children: ReactNode;
}

export function Routes({ children }: RoutesProps) {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('Routes must be used within a MemoryRouter.');
  }

  const { pathname } = context.location;
  let matchedElement: ReactNode = null;

  Children.forEach(children, (child) => {
    if (matchedElement !== null) {
      return;
    }

    if (!child || typeof child !== 'object') {
      return;
    }

    const element = child as ReactElement<RouteProps>;
    if (element.type !== Route) {
      return;
    }

    if (element.props.path === pathname) {
      matchedElement = element.props.element;
    }
  });

  return <>{matchedElement}</>;
}

interface NavLinkProps {
  to: string;
  children: ReactNode;
  className?: string;
}

export function NavLink({ to, children, className = '' }: NavLinkProps) {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('NavLink must be used within a MemoryRouter.');
  }

  const { location, navigate } = context;
  const destination = to.startsWith('/') ? to : `/${to}`;
  const isActive = location.pathname === destination;
  const combinedClassName = `${className}${isActive ? "" : ''}`;

  return (
    <a
      href={destination}
      aria-current={isActive ? 'page' : undefined}
      className={combinedClassName}
      onClick={(event) => {
        event.preventDefault();
        navigate(destination);
      }}
    >
      {children}
    </a>
  );
}

export function useLocation(): RouterLocation {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useLocation must be used within a MemoryRouter.');
  }
  return context.location;
}

export function useNavigate(): (to: string) => void {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useNavigate must be used within a MemoryRouter.');
  }
  return context.navigate;
}
